import React from 'react';

export const WhyLadyHustlerSection = () => {
  const transforms = [
    { from: 'Constraint', to: 'Strategy' },
    { from: 'Community', to: 'Leverage' },
    { from: 'Knowledge', to: 'Access' },
    { from: 'Access', to: 'Opportunity' }
  ];

  const honorCards = [
    'The entrepreneur building with limited capital',
    'The professional balancing ambition and care work',
    'The woman rebuilding after displacement or crisis',
    'The leader creating impact without institutional backing'
  ];

  return (
    <section id="why-lady-hustler" className="py-20" style={{ backgroundColor: 'var(--pink-blush)' }}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-8">
            <div className="decorative-rule w-16"></div>
            <h2 className="lhsc-heading text-3xl md:text-4xl mx-6">WHY "LADY HUSTLER"?</h2>
            <div className="decorative-rule w-16"></div>
          </div>
        </div>

        {/* Part 1: The Surface Truth */}
        <div className="lhsc-body space-y-6 mb-12">
          <p>
            While the world sells perfection online, women are navigating rising living costs, caregiving responsibilities, 
            unstable economies, and systems not designed for them to win. Yet every day, women still build. They adapt. They lead. 
            They hustle — with dignity.
          </p>
          <p>
            A Lady Hustler is not defined by grind culture. She is defined by resourcefulness, courage, and collective intelligence. 
            We are here to rewrite the story — to turn survival into strategy, and ambition into shared progress.
          </p>
        </div>

        {/* Part 2: What It Really Means (White Inset Card) */}
        <div className="bg-white p-8 md:p-12 border border-[var(--pink-dusty)] mb-12">
          <h3 className="lhsc-heading text-2xl mb-6 text-center">What It Really Means</h3>
          <div className="lhsc-body space-y-6 mb-8">
            <p>
              A Lady Hustler is not someone who works harder than everyone else. She is someone who moves consciously inside 
              real conditions — her own, and others'. She understands that hustle looks different depending on where you start.
            </p>
            <p>
              Some of us have stable internet, a roof, education, networks, and proximity to opportunity. And some women — 
              often living right next to us — do not. Lady Hustler begins with awareness. Awareness that access is uneven. 
              That opportunity is not distributed fairly. That what feels "normal" to one woman can be unreachable to another.
            </p>
            <p>
              A Lady Hustler recognizes her privileges without guilt, and her challenges without comparison.
            </p>
          </div>

          {/* Transform Grid */}
          <div className="mb-8">
            <h4 className="lhsc-subheading text-center mb-6 text-base">She Turns</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {transforms.map((transform, index) => (
                <div key={index} className="flex items-center justify-center space-x-4">
                  <span className="lhsc-heading text-lg" style={{ color: 'var(--mauve-rose)' }}>
                    {transform.from}
                  </span>
                  <span style={{ color: 'var(--slate-grey)' }}>→</span>
                  <span className="lhsc-heading text-lg" style={{ color: 'var(--mauve-rose)' }}>
                    {transform.to}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lhsc-body">
            <p>
              And when opportunity grows, she understands that growth is stronger when it is shared. Not as obligation. 
              Not as charity. But as a choice. Lady Hustler is a way of moving through the world — with awareness, integrity, 
              and the belief that progress is more powerful when it includes others.
            </p>
          </div>
        </div>

        {/* Four Honor Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {honorCards.map((card, index) => (
            <div
              key={index}
              className="bg-white p-6 border border-[var(--pink-dusty)] text-center"
            >
              <p className="lhsc-body">{card}</p>
            </div>
          ))}
        </div>

        {/* Why This Name Matters */}
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="lhsc-heading text-xl mb-4">Why This Name Matters</h3>
          <p className="lhsc-body">
            We didn't choose "Lady Hustler" to be trendy. We chose it because it tells the truth. It reflects women who 
            don't wait for permission — but who also believe that success should never be built alone.
          </p>
        </div>
      </div>
    </section>
  );
};
